/* eslint-disable no-underscore-dangle */
import React, {useState, useRef, useEffect} from 'react';
import Square from './AnnotationShapes/Square';
import Arrow from './AnnotationShapes/Arrow';
import Constants, {AnnotationModes} from '../../constants/Constants';
import {fabric} from 'fabric-webpack';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {mediaInterface} from '@techsmith/techsmith-smartplayer';
import {IComment} from '../../model/commentModel';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {selectedConversationStore} from '../../context/selectedConversationProvider';
import {updateCanvasState} from '../../context/canvasProvider';
import {annotationManagerStore, updateAnnotationManagerState} from '../../context/annotationManagerProvider';
import {commentsStore} from '../../context/commentsProvider';
import {AriaAlert} from '../util/StyledElements';
import {updateQuizData} from '../../context/quizDataProvider';
import {mediaStore} from '../../context/mediaProvider';

export const AnnotationWrapper = styled.div<{mediaWidth: number; mediaHeight: number; pointerEvents: string}>`
   display: flex;
   flex-direction: column;
   flex-shrink: 0; /* this forces the edit annotations message to overflow appropriately */
   width: 100% !important;
   height: 100%;
   pointer-events: ${props => props.pointerEvents};

   .fullscreen-mode & {
      height: ${props => `calc(100vw / ${props.mediaWidth / props.mediaHeight}) !important`};
      max-width: 100vw;
      align-self: center;
   }

   .canvas-container {
      margin: auto;
   }
`;

const AnnotationCanvasWrapper = styled.div`
   display: flex;
   flex-direction: column;
   flex-shrink: 0;
   height: 100%;
`;

export interface IStateMappedProps {
   SelectedComment?: IComment;
   annotationMode: AnnotationModes;
   comments: IComment[];
   IsImage: boolean;
}

export const AnnotationBase: React.FC<IStateMappedProps> = ({SelectedComment, annotationMode, comments, IsImage}) => {
   const {t} = useTranslation();
   const [pointOfOrigin] = useState('center');
   const canvasRef = useRef(null);
   const mediaContentRef = useRef(document.querySelector('#tscMediaContent .video-div') ?? document.querySelector('#tscMediaContent .smart-player-image-media'));

   const resetCenter = (item: any): void => {
      if (!item.getPointByOrigin) {
         return;
      }

      const coords = item.getPointByOrigin(pointOfOrigin, pointOfOrigin);
      item.left = coords.x;
      item.top = coords.y;
      item.originX = pointOfOrigin;
      item.originY = pointOfOrigin;
   };

   const lockAnnotations = (): void => {
      canvasRef.current.getObjects().forEach((item: any) => {
         item.lockMovementX = true;
         item.lockMovementY = true;
         item.lockRotation = true;
         item.lockScalingX = true;
         item.lockScalingY = true;
         item.hasBorders = false;
         item.hasControls = false;
         item.hoverCursor = Constants.cursors.default;

         if (item.animate) {
            resetCenter(item);
            const origScaleX = item.scaleX;
            const origScaleY = item.scaleY;
            item.scaleX = item.scaleX * Constants.annotation.preloadAnnotationScale;
            item.scaleY = item.scaleY * Constants.annotation.preloadAnnotationScale;
            item.opacity = 0;
            item.animate({opacity: 1, scaleX: origScaleX, scaleY: origScaleY}, {
               duration: Constants.annotation.animationDuration,
               onChange: canvasRef.current.renderAll.bind(canvasRef.current)
            });
         }
      });
   };

   const fireOffAnnotationAccessibilityAlert = () => {
      document.getElementById('annotation-exists-alert').style.display = 'block';

      setTimeout(() => {
         document.getElementById('annotation-exists-alert').style.display = 'none';
      }, 1000);
   };

   const loadAnnotations = (): void => {
      const topLevelCommentAnnotation = SelectedComment?.parentCommentId ? comments.find(c => c.id === SelectedComment.parentCommentId).annotation : SelectedComment?.annotation;

      if (topLevelCommentAnnotation) {
         fireOffAnnotationAccessibilityAlert();
         canvasRef.current.loadFromJSON(topLevelCommentAnnotation);
         lockAnnotations();
      }
   };

   const isObjectOffCanvas = (top: number, left: number, bottom: number, right: number): boolean => {
      const offset = Constants.annotation.controlsPadding;

      const pastLeft = right < offset;
      const pastTop = bottom < offset;
      const pastRight = left > canvasRef.current.width - offset;
      const pastBottom = top > canvasRef.current.height - offset;

      return pastLeft || pastTop || pastRight || pastBottom;
   };

   const removeOutOfViewObjects = (): void => {
      canvasRef.current.getObjects().forEach((shapes: any) => {
         const bounds = shapes.getBoundingRect();
         if (isObjectOffCanvas(bounds.top, bounds.left, (bounds.top as number) + (bounds.height as number), (bounds.left as number) + (bounds.width as number))) {
            canvasRef.current.remove(shapes);

            if (canvasRef.current.getObjects().length === 0) {
               updateAnnotationManagerState({annotationMode: AnnotationModes.none});
            }
         }
      });
   };

   const clearCanvas = (): void => {
      canvasRef.current.clear();
   };

   const ensureCanvasDimensionsAreSet = () => {
      if (canvasRef.current.width === 0) {
         canvasRef.current.setWidth(document.getElementById('tscMediaContent').clientWidth);
      }

      if (canvasRef.current.height === 0) {
         canvasRef.current.setHeight(document.getElementById('tscMediaContent').clientHeight);
      }
      rescale();
   };

   const saveCanvasChange = (): void => {
      removeOutOfViewObjects();
   };

   const changeShapeColor = (shape: any, color: string): void => {
      shape.stroke = color;

      if (shape.fill !== Constants.annotation.transparentColor) {
         shape.fill = color;
      }
   };

   const onColorChange = (color: string): void => {
      canvasRef.current.freeDrawingBrush.color = color;

      const selectedShape = canvasRef.current.getActiveObject();
      const selectedShapes = canvasRef.current.getActiveGroup();

      if (selectedShape) {
         changeShapeColor(selectedShape, color);
      }

      if (selectedShapes) {
         selectedShapes._objects.forEach((shape: any) => {
            changeShapeColor(shape, color);
         });
      }

      canvasRef.current.renderAll();
      canvasRef.current.trigger('object:modified', {target: {}});
   };

   const rescale = (): void => {
      const scaleRatio = Math.max(mediaContentRef.current.clientWidth, mediaContentRef.current.clientHeight) / Constants.annotation.scale;

      if (scaleRatio <= 0) {
         return;
      }

      canvasRef.current.setZoom(scaleRatio);
      canvasRef.current.strokeScale = Constants.annotation.scale / Math.max(mediaContentRef.current.clientWidth, mediaContentRef.current.clientHeight);
      const strokeWidth = canvasRef.current.strokeScale * Constants.annotation.strokeWidth;
      canvasRef.current.freeDrawingBrush.width = strokeWidth;
      canvasRef.current.getObjects().forEach((obj: any) => {
         obj.strokeWidth = strokeWidth;
      });
      canvasRef.current.renderAll();
   };

   const onTouchStart = (e: any) => {
      if (annotationMode === AnnotationModes.edit) {
         e.preventDefault();
      }
   };

   const onSelectionCreated = (e: any): void => {
      if (e.target.type === 'group') {
         e.target.hasControls = false;
      }
   };

   useEffect(() => {
      canvasRef.current = new fabric.Canvas('canvas-element', {
         uniScaleTransform: true,
         selection: annotationMode === AnnotationModes.edit
      });

      canvasRef.current.on('colorchange', onColorChange);
      canvasRef.current.on('object:added', () => {
         ensureCanvasDimensionsAreSet();
         saveCanvasChange();
      });
      canvasRef.current.on('object:removed', saveCanvasChange);
      canvasRef.current.on('object:modified', saveCanvasChange);
      canvasRef.current.on('selection:created', onSelectionCreated);
      canvasRef.current.on('touchstart', onTouchStart, {passive: false});
      canvasRef.current.defaultCursor = 'pointer';
      Square.initializeEventListeners(canvasRef.current);
      Arrow.initializeEventListeners(canvasRef.current);
      updateCanvasState({canvasRef: canvasRef.current});
      canvasRef.current.setHeight(mediaContentRef.current.clientHeight);
      canvasRef.current.setWidth(mediaContentRef.current.clientWidth);
      rescale();

      return () => {
         updateQuizData({IsQuizzing: false});
         canvasRef.current.off('touchstart', onTouchStart);
         canvasRef.current.dispose();
      };
   }, []);

   const onKeyDown = (e: any): void => {
      const isEditAnnotationMode = annotationMode === AnnotationModes.edit;
      const isDeleteOrBackspaceEvent = e.keyCode === Constants.keys.backspace || e.keyCode === Constants.keys.delete;
      const editingInputTypes = [Constants.elementTypes.input, Constants.elementTypes.textArea, Constants.elementTypes.search];
      const isEditingText = editingInputTypes.some(type => e.target.type === type);

      if (!isEditAnnotationMode || !isDeleteOrBackspaceEvent || isEditingText) {
         return;
      }

      const activeObject = canvasRef.current.getActiveObject();
      const activeGroup = canvasRef.current.getActiveGroup();

      if (!activeObject && !activeGroup) {
         return;
      }

      e.preventDefault(); // Do not trigger navigation back if we are deleting an object
      if (activeGroup) {
         activeGroup._objects.forEach((shape: any) => {
            canvasRef.current.remove(shape);
         });
      }

      if (activeObject) {
         canvasRef.current.remove(activeObject);
      }

      canvasRef.current.deactivateAll();
      canvasRef.current.renderAll();

      if (canvasRef.current.getObjects().length === 0) {
         updateAnnotationManagerState({annotationMode: AnnotationModes.none});
      }
   };

   useEffect(() => {
      clearCanvas();
      window.addEventListener('keydown', onKeyDown);

      if (annotationMode === AnnotationModes.edit) {
         mediaInterface.singleClickControlEnabled = false;
         mediaInterface.doubleClickControlEnabled = false;
         mediaInterface.pause();
      } else {
         mediaInterface.singleClickControlEnabled = true;
         mediaInterface.doubleClickControlEnabled = true;
         canvasRef.current.off('mouse:down');
         canvasRef.current.off('mouse:move');
         canvasRef.current.off('mouse:up');
      }

      if (annotationMode === AnnotationModes.display) {
         loadAnnotations();
      }

      canvasRef.current.selection = annotationMode === AnnotationModes.edit;
      canvasRef.current.renderAll();

      return () => {
         window.removeEventListener('keydown', onKeyDown);
      };
   }, [SelectedComment?.annotation, annotationMode, mediaInterface.currentTime]);

   useEffect(() => {
      // should be only 1, default to video
      // this usage is the requirement for `@types/resize-observer-browser`, once we get up to >TS4.5.x (or whatever 4.x version
      // added the type) we can drop that include from package.json
      const observer = new ResizeObserver(() => {
         if (!canvasRef || !mediaContentRef || !canvasRef.current.setWidth || !canvasRef.current.setHeight) {
            return;
         }

         canvasRef.current.setWidth(mediaContentRef.current.clientWidth);
         canvasRef.current.setHeight(mediaContentRef.current.clientHeight);
         rescale();
      });
      observer.observe(mediaContentRef.current);
      return () => {
         observer.disconnect();
      };
   }, [mediaContentRef]);

   const style: any = {
      width: '100%',
      height: '100%',
      margin: 'auto'
   };

   if (IsImage) {
      style.position = 'absolute';
      style.left = 0;
      style.right = 0;
      style.top = 0;
      style.bottom = 0;
   }

   return (
      <AnnotationWrapper mediaWidth={mediaContentRef.current.clientWidth} mediaHeight={mediaContentRef.current.clientHeight} pointerEvents={annotationMode === AnnotationModes.edit ? 'auto' : 'none'}>
         <AnnotationCanvasWrapper>
            <canvas id="canvas-element" ref={canvasRef} style={style} />
         </AnnotationCanvasWrapper>
         <AriaAlert role="alert" id="annotation-exists-alert">{t('annotation.annotationExistsAlert')}</AriaAlert>
      </AnnotationWrapper>
   );
};

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {SelectedComment}: Partial<IStateMappedProps>,
   {annotationMode}: Partial<IStateMappedProps>,
   {comments}: Partial<IStateMappedProps>,
   {IsImage}: Partial<IStateMappedProps>
): IStateMappedProps => ({SelectedComment, annotationMode, comments, IsImage});

export default withMemoizedContexts(mapStatesToProps, selectedConversationStore, annotationManagerStore, commentsStore, mediaStore)(AnnotationBase);

